// bluaIQ — Diagnostic Takeoff v3 — DiagnosticTakeoffModule
// Top-level UI shell. Reads /api/projects/:projectId/takeoff-diagnostic
// and renders the safety badges, source documents, runs, artifacts and
// blocker / confidence / no-silent-drop placeholders.
//
// SAFETY: this module never renders quantities, BOM, BOQ, or "approve"
// affordances. It exists alongside legacy TakeoffModule.tsx; nothing
// shown here may flow into legacy facts.

import { apiFetch } from '../../utils/api'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FilePlus2,
  Loader2,
  PlayCircle,
  ShieldCheck,
  UploadCloud,
  FileSearch,
} from 'lucide-react'
import DiagnosticSafetyBadges from './DiagnosticSafetyBadges'
import DiagnosticArtifactList from './DiagnosticArtifactList'
import DiagnosticBlockerPanel from './DiagnosticBlockerPanel'
import SourceDocumentReviewPlaceholder from './SourceDocumentReviewPlaceholder'
import type {
  DiagnosticTakeoffModuleSnapshot,
  SourceDocumentIdentity,
} from '../../takeoff-diagnostic-v3/types'

interface SnapshotEnvelope extends DiagnosticTakeoffModuleSnapshot {
  safety_flags?: Record<string, unknown>
}

const NOT_ALLOWED_LABELS = [
  'No final quantities',
  'No Step B',
  'No Gate 3',
  'No BOQ',
  'No purchase quantities',
  'No accepted MeasurementEvidence',
] as const

function fallbackSnapshot(projectId: string): DiagnosticTakeoffModuleSnapshot {
  return {
    projectId,
    safetyFlags: {
      diagnostic_only: true,
      diagnostic_v3_enabled: true,
      legacy_enabled: true,
      takeoff_engine: 'legacy',
      final_quantities_allowed: false,
      gate_3_allowed: false,
      step_b_allowed: false,
      real_step_b_allowed: false,
      boq_allowed: false,
      purchase_quantity_allowed: false,
      quantity_allowed: false,
      accepted_measurement_evidence_allowed: false,
      accepted_final_quantity_allowed: false,
      accepted_parent_boundary_allowed: false,
      accepted_local_segment_width_allowed: false,
      accepted_opening_quantities_allowed: false,
      final_scale_lock_record_allowed: false,
      takeoff_preview_allowed: false,
    },
    sourceDocuments: [],
    runs: [],
    artifacts: [],
    reviews: [],
    notesForChatGPT: [
      'API unavailable — rendering empty diagnostic-only shell.',
      'No candidate items, no quantities, no Step B, no Gate 3.',
    ],
  }
}

export default function DiagnosticTakeoffModule() {
  const { projectId = '' } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const [activeDocId, setActiveDocId] = useState<string | null>(null)

  const { data, isLoading } = useQuery<DiagnosticTakeoffModuleSnapshot>({
    queryKey: ['diagnostic-takeoff-v3-snapshot', projectId],
    queryFn: async () => {
      const res = await apiFetch(`/api/projects/${projectId}/takeoff-diagnostic`)
      if (!res.ok) throw new Error('snapshot-failed')
      const body = (await res.json()) as SnapshotEnvelope
      return body
    },
    retry: false,
    staleTime: 15_000,
  })

  const snapshot = data ?? fallbackSnapshot(projectId)
  const wireFlags = (data as SnapshotEnvelope | undefined)?.safety_flags ?? snapshot.safetyFlags

  const activeDoc =
    snapshot.sourceDocuments.find((d) => d.id === activeDocId) ?? snapshot.sourceDocuments[0] ?? null
  const activeReview =
    activeDoc ? snapshot.reviews.find((r) => r.sourceDocumentId === activeDoc.id) ?? null : null

  return (
    <div className="p-5 max-w-5xl mx-auto space-y-5">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <FileSearch size={20} className="text-[#1C1A17]" />
          <h2 className="text-[26px] md:text-[32px] font-[900] tracking-[-1px] text-[#1C1A17]">
            Diagnostic Takeoff v3
          </h2>
          <span className="inline-flex items-center gap-1 text-[9px] font-[800] text-green-700 bg-green-50 border border-green-100 rounded-full px-2 py-0.5">
            <ShieldCheck size={10} /> diagnostic-only
          </span>
        </div>
        <p className="text-[13px] font-[600] text-[#9E9890]">
          Candidate-only diagnostic surface. Runs alongside the legacy takeoff —
          never replaces it, never produces accepted quantities.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {NOT_ALLOWED_LABELS.map((label) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 text-[9px] font-[800] text-red-700 bg-red-50 border border-red-100 rounded-full px-2 py-0.5"
            >
              {label}
            </span>
          ))}
        </div>
      </header>

      <DiagnosticSafetyBadges flags={wireFlags as Record<string, unknown>} />

      <UploadSection
        projectId={projectId}
        onUploaded={(doc) => {
          setActiveDocId(doc.id)
          queryClient.invalidateQueries({
            queryKey: ['diagnostic-takeoff-v3-snapshot', projectId],
          })
        }}
      />

      <section className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
            Source documents ({snapshot.sourceDocuments.length})
          </div>
          {snapshot.sourceDocuments.length === 0 ? (
            <EmptyCard label="No source documents yet" />
          ) : (
            <ul className="space-y-2">
              {snapshot.sourceDocuments.map((doc) => (
                <li key={doc.id}>
                  <button
                    type="button"
                    onClick={() => setActiveDocId(doc.id)}
                    className={`w-full text-left bg-white border rounded-xl p-3 hover:bg-[#FAFAF8] transition-colors ${
                      activeDoc?.id === doc.id ? 'border-[#1C1A17]' : 'border-[#ECEAE4]'
                    }`}
                  >
                    <div className="text-[12px] font-[800] text-[#1C1A17] truncate">
                      {doc.originalFilename ?? 'Untitled source document'}
                    </div>
                    <div className="text-[10px] font-[600] text-[#9E9890] truncate">
                      {doc.contentHash ? `sha256: ${doc.contentHash.slice(0, 16)}…` : 'no content hash'}
                      {doc.fileSizeBytes != null ? ` · ${formatBytes(doc.fileSizeBytes)}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-3">
          <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
            Diagnostic runs ({snapshot.runs.length})
          </div>
          {snapshot.runs.length === 0 ? (
            <EmptyCard label="No diagnostic runs yet" />
          ) : (
            <ul className="space-y-2">
              {snapshot.runs.map((run) => (
                <li key={run.id} className="bg-white border border-[#ECEAE4] rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-[800] text-[#1C1A17]">
                      {run.engineVersion}
                    </span>
                    <span className="inline-flex items-center text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] bg-[#F2F0EB] px-2 py-0.5 rounded-full">
                      {run.status}
                    </span>
                  </div>
                  <div className="text-[10px] font-[600] text-[#9E9890] mt-1">
                    src: {run.sourceDocumentId.slice(0, 12)}… ·{' '}
                    {new Date(run.createdAt).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {activeDoc && (
            <RunActionRow
              projectId={projectId}
              document={activeDoc}
              onCreated={() => {
                queryClient.invalidateQueries({
                  queryKey: ['diagnostic-takeoff-v3-snapshot', projectId],
                })
              }}
            />
          )}
        </div>
      </section>

      {activeDoc && (
        <section>
          <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-2">
            Source-document review (placeholder)
          </div>
          <SourceDocumentReviewPlaceholder
            projectId={projectId}
            document={activeDoc}
            review={activeReview}
          />
        </section>
      )}

      <section className="space-y-2">
        <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
          Diagnostic artifacts ({snapshot.artifacts.length})
        </div>
        <DiagnosticArtifactList artifacts={snapshot.artifacts} />
      </section>

      <DiagnosticBlockerPanel artifacts={snapshot.artifacts} />

      {isLoading && (
        <div className="text-[11px] font-[700] text-[#9E9890] flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> Loading diagnostic snapshot…
        </div>
      )}

      <footer className="text-[10px] font-[600] text-[#9E9890] border-t border-[#ECEAE4] pt-3">
        Notes for ChatGPT review:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          {snapshot.notesForChatGPT.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </footer>
    </div>
  )
}

function EmptyCard({ label }: { label: string }) {
  return (
    <div className="text-center py-8 border border-dashed border-[#ECEAE4] rounded-xl">
      <p className="text-[12px] font-[700] text-[#9E9890]">{label}</p>
    </div>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function UploadSection({
  projectId,
  onUploaded,
}: {
  projectId: string
  onUploaded: (doc: SourceDocumentIdentity) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('no-file')
      const fd = new FormData()
      fd.append('file', file)
      const res = await apiFetch(
        `/api/projects/${projectId}/takeoff-diagnostic/source-documents`,
        { method: 'POST', body: fd },
      )
      if (!res.ok) {
        let message = 'upload-failed'
        try {
          const body = await res.json()
          message = typeof body?.error === 'string' ? body.error : message
        } catch (_) { /* ignore */ }
        throw new Error(message)
      }
      const body = await res.json()
      return body.item as SourceDocumentIdentity
    },
    onSuccess: (doc) => {
      setFile(null)
      setUploadError(null)
      onUploaded(doc)
    },
    onError: (err: Error) => {
      setUploadError(err.message)
    },
  })

  return (
    <section className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <UploadCloud size={16} className="text-[#9E9890]" />
        <span className="text-[13px] font-[800] text-[#1C1A17]">Upload source PDF</span>
        <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] bg-[#F2F0EB] px-2 py-0.5 rounded-full">
          PDF only · diagnostic-only
        </span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => {
            setUploadError(null)
            setFile(e.target.files?.[0] ?? null)
          }}
          className="text-[12px] font-[600]"
        />
        <button
          type="button"
          disabled={!file || mutation.isPending}
          onClick={() => mutation.mutate()}
          className="bg-[#1C1A17] text-white rounded-full px-4 py-2 text-[11px] font-[800] disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {mutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <FilePlus2 size={12} />}
          {mutation.isPending ? 'Uploading…' : 'Register source'}
        </button>
      </div>
      {uploadError && (
        <div className="text-[11px] font-[700] text-red-600">Upload failed: {uploadError}</div>
      )}
      <div className="text-[10px] font-[600] text-[#9E9890]">
        The source document is stored as identity + sha-256 only in this milestone. No
        AI is called. No measurements are produced.
      </div>
    </section>
  )
}

function RunActionRow({
  projectId,
  document,
  onCreated,
}: {
  projectId: string
  document: SourceDocumentIdentity
  onCreated: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(
        `/api/projects/${projectId}/takeoff-diagnostic/runs`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sourceDocumentId: document.id }),
        },
      )
      if (!res.ok) {
        let message = 'run-failed'
        try {
          const body = await res.json()
          message = typeof body?.error === 'string' ? body.error : message
        } catch (_) { /* ignore */ }
        throw new Error(message)
      }
      return res.json()
    },
    onSuccess: () => {
      setError(null)
      onCreated()
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-3 space-y-2">
      <div className="text-[11px] font-[700] text-[#1C1A17]">
        Schedule a diagnostic run on{' '}
        <span className="font-[800]">{document.originalFilename ?? document.id.slice(0, 8)}</span>
      </div>
      <div className="text-[10px] font-[600] text-[#9E9890]">
        Creates placeholder artifacts only. No AI, no Python engine, no quantity output.
      </div>
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="bg-[#1C1A17] text-white rounded-full px-4 py-2 text-[11px] font-[800] disabled:opacity-40 inline-flex items-center gap-1.5"
      >
        {mutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
        {mutation.isPending ? 'Scheduling…' : 'Schedule diagnostic run'}
      </button>
      {error && (
        <div className="text-[11px] font-[700] text-red-600">Run failed: {error}</div>
      )}
    </div>
  )
}
